/**
 * Self-service organisation onboarding wizard.
 *
 * Shown to brand-new organisers (`role` in {`org_admin`, `event_manager`})
 * who don't yet have an ``organization_id`` linked.  Until they complete
 * this, the rest of the dashboard is gated (see ``OrganisationOnboardingGate``)
 * so they can't stumble into half-broken pages or — much worse — be
 * misled into believing they're a member of an existing tenant.  This
 * was the Feb 2026 production incident where new organisers were seeing
 * globex.org's helpdesk tickets / CRM broadcasts.
 *
 * Two tabs:
 *   1. **Create new organisation** — name + tagline + contact, lands on
 *      14-day Starter trial with 1 event + watermarked PDFs.
 *   2. **Join an existing organisation** — paste invite code OR public
 *      org slug.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const PRIMARY = "#0F4C81";
const GOLD = "#D4AF37";
const NAVY = "#0A192F";

function TabButton({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold rounded-full transition-all ${
        active
          ? "bg-[#D4AF37] text-[#0A192F]"
          : "bg-transparent text-white/60 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function TrialBadge() {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] uppercase tracking-[0.22em] font-bold"
      data-testid="trial-badge"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
      14-day Starter trial
    </div>
  );
}

export default function OrganisationOnboardingPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("create");
  const [submitting, setSubmitting] = useState(false);

  // Create form state
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");

  // Join form state
  const [inviteCode, setInviteCode] = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Please enter your organisation name.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orgs/onboarding/create", {
        name: name.trim(),
        tagline: tagline.trim() || null,
        contact_email: contactEmail.trim() || null,
        country: country.trim() || null,
        website: website.trim() || null,
      });
      toast.success(`Welcome to ${data.organisation.name} — 14-day trial active.`);
      // Refresh auth context so the dashboard sees the new organization_id.
      if (refresh) await refresh();
      navigate(data.next || "/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not create the organisation.";
      // Pydantic validation errors arrive as a list.
      if (Array.isArray(msg)) {
        toast.error(msg.map((m) => m.msg || m).join(" · "));
      } else {
        toast.error(String(msg));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    const code = inviteCode.trim();
    if (!code) {
      toast.error("Paste the invite code or organisation slug your team sent you.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orgs/onboarding/join", { invite_code: code });
      toast.success(`You're now part of ${data.organisation.name}.`);
      if (refresh) await refresh();
      navigate(data.next || "/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "We couldn't find that invite.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative"
      style={{ background: NAVY }}
      data-testid="organisation-onboarding-page"
    >
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 25%, rgba(212,175,55,0.6) 0, transparent 50%), radial-gradient(circle at 75% 75%, rgba(15,76,129,0.6) 0, transparent 50%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 py-10">
        {/* Header */}
        <header className="flex items-center justify-between mb-12">
          <BrandLogo light linkable={false} />
          <TrialBadge />
        </header>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
          {/* LEFT: editorial intro */}
          <section>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold mb-5">
              ONE MORE STEP · ORGANISER ONBOARDING
            </p>
            <h1 className="font-display text-5xl lg:text-6xl text-white leading-[1.05] mb-6">
              Let&apos;s set up
              <br />
              <span className="italic font-medium" style={{ color: GOLD }}>
                your conference HQ.
              </span>
            </h1>
            <p className="text-white/70 text-base leading-relaxed max-w-md">
              You signed up as an <strong className="text-white">organiser</strong> — your
              workspace lives inside an organisation. Create one now, or join
              the one your team has already set up.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { k: "1 event", v: "Run your first conference end-to-end on the trial." },
                { k: "50 delegates", v: "Onboard a full committee without paying upfront." },
                { k: "AI Prep ready", v: "100 Diploks pre-loaded so delegates can prep." },
                { k: "Watermarked PDFs", v: "Certificates work — branding lifts on upgrade." },
              ].map((row) => (
                <div key={row.k} className="grid grid-cols-[minmax(0,170px)_1fr] items-start gap-4 sm:gap-5">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold leading-snug break-words">
                    {row.k}
                  </span>
                  <span className="text-white/70 text-sm leading-relaxed min-w-0">{row.v}</span>
                </div>
              ))}
            </div>
          </section>

          {/* RIGHT: the form card */}
          <section
            className="relative rounded-3xl border border-white/10 backdrop-blur-xl p-8"
            style={{ background: "rgba(255,255,255,0.04)" }}
          >
            <div className="flex gap-2 mb-6">
              <TabButton
                active={tab === "create"}
                onClick={() => setTab("create")}
                label="Create new"
                testid="onboarding-tab-create"
              />
              <TabButton
                active={tab === "join"}
                onClick={() => setTab("join")}
                label="Join existing"
                testid="onboarding-tab-join"
              />
            </div>

            {tab === "create" ? (
              <form onSubmit={handleCreate} className="space-y-4" data-testid="onboarding-create-form">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    Organisation name <span className="text-[#D4AF37]">*</span>
                  </Label>
                  <Input
                    data-testid="onboarding-create-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Westwood International School"
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    required
                  />
                </div>

                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    One-line tagline
                  </Label>
                  <Input
                    data-testid="onboarding-create-tagline"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Conferences that grow global citizens"
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                      Contact email
                    </Label>
                    <Input
                      data-testid="onboarding-create-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="conferences@your-org.edu"
                      className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                      Country
                    </Label>
                    <Input
                      data-testid="onboarding-create-country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="India"
                      className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    Website
                  </Label>
                  <Input
                    data-testid="onboarding-create-website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://your-org.edu"
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-2 bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A192F] font-bold tracking-wide uppercase text-xs py-6 rounded-lg"
                  data-testid="onboarding-create-submit"
                >
                  {submitting ? "Setting up…" : "Activate my trial"}
                </Button>
                <p className="text-[11px] text-white/40 text-center pt-2">
                  No credit card. Upgrade any time from the billing page.
                </p>
              </form>
            ) : (
              <form onSubmit={handleJoin} className="space-y-4" data-testid="onboarding-join-form">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    Invite code or organisation slug <span className="text-[#D4AF37]">*</span>
                  </Label>
                  <Input
                    data-testid="onboarding-join-code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="e.g. WMUN-2026 or westwood-international"
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    required
                  />
                  <p className="text-[11px] text-white/40 mt-2">
                    Ask your existing organiser for the invite code that was sent to your email,
                    or the public slug shown on your organisation&apos;s microsite.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold tracking-wide uppercase text-xs py-6 rounded-lg"
                  data-testid="onboarding-join-submit"
                >
                  {submitting ? "Joining…" : "Join organisation"}
                </Button>
                <p className="text-[11px] text-white/40 text-center pt-2">
                  Once joined, your role will mirror the invite (or stay as organiser).
                </p>
              </form>
            )}
          </section>
        </div>

        <footer className="mt-16 pt-6 border-t border-white/5 flex justify-between items-center text-[11px] uppercase tracking-[0.2em] text-white/40">
          <span>Diplomexa · Conference OS</span>
          <span>v3.0</span>
        </footer>
      </div>
    </div>
  );
}
